import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { ILoginRequest } from '@core/interfaces/login-request';
import { IResponse } from '@core/interfaces/response.interface';
import { ISign } from '@core/interfaces/sign.interface';
import { TokenService } from '@core/services/token.service';
import { getDeviceInfo } from '@core/utils';
import { environment as env } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);

  endSession$ = new Subject<string>();
  temporaryToken: string;
  oldTemporaryToken: string;
  rsaPublicKey: string | null = null;
  stepTempTokens = {
    newPassword: null,
    firstPassword: null,
    twoFaRegister: null,
    twoFa: null,
  };

  get isAuthenticated(): boolean {
    return !!this.tokenService.accessToken && !!this.tokenService.refreshToken;
  }

  hello(): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.hello}`, getDeviceInfo());
  }

  login(loginData: ILoginRequest): Observable<IResponse<null>> {
    return this.http.post<IResponse<null>>(`${env.webApi}/${env.api.identities}/${env.api.authenticate}`, loginData);
  }

  setPassword(passwords: ILoginRequest): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.setPassword}`, passwords);
  }

  enterSignVerify(sign: ISign): Observable<any> {
    return this.http.post<any>(`${env.webApi}/${env.api.identities}/${env.api.enter2FaSign}`, sign);
  }

  refresh(companyId?: string): Observable<IResponse<any>> {
    const data = this.tokenService.getTokens();
    if (companyId) {
      data['companyId'] = companyId;
    }
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.refresh}`, data);
  }

  generateQr(): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.generateQr}`, {});
  }

  setTwoFaCode(code: string): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.enterFaCode}`, {
      sign: { code },
    });
  }

  signerPing(): Observable<any> {
    const headers = new HttpHeaders();
    return this.http.post<any>(`${env.signerPath}/${env.api.ping}`, {}, { headers });
  }

  logout(): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.logout}`, {});
  }

  newPassword(passwords: ILoginRequest): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.changePassword}`, passwords);
  }

  forgotPassword(data: FormData): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(`${env.webApi}/${env.api.identities}/${env.api.forgotPassword}`, data);
  }

  enterCodeForgotPassword(code: ISign): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(
      `${env.webApi}/${env.api.identities}/${env.api.forgotPassword}/${env.api.enterFaCode}`,
      code
    );
  }
}
